import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isStaff, isSuperAdmin } from '@foundry/shared';
import { useMe } from '../../api/auth.js';
import { ApiError } from '../../api/client.js';
import {
  useAssignDeveloper,
  useAssignments,
  useCreateStory,
  useDeleteStory,
  useUnassignDeveloper,
  useGenerateSpec,
  useGenerateStories,
  useProject,
  useProjectAudit,
  useQuestionnaire,
  useReorderStories,
  useResetStories,
  useWipeRepo,
  useSetRepository,
  useSpecDecision,
  useHoursStatus,
  useMarkMessagesRead,
  useProjectAttention,
  useSpecification,
  useStories,
  useStoryDecision,
  useUpdateStory,
} from '../../api/projects.js';
import {
  useBatchBuild,
  useBuilds,
  useDeployment,
  useRedeploy,
  useStartBuild,
  useUsage,
} from '../../api/builds.js';
import {
  useAssignStoriesToEpic,
  useCreateEpic,
  useDeleteEpic,
  useEpics,
  useRenameEpic,
  useUnassignedStories,
} from '../../api/epics.js';
import type { Build, Project, Story } from '../../api/types.js';
import { Alert, Button, Card, Field, Spinner, TextArea } from '../../components/ui.js';
import { useConfirm } from '../../components/ConfirmDialog.js';
import { SpecView } from '../../components/SpecView.js';
import { ProjectMessages, SpecComments, StoryComments } from '../../components/Discussion.js';
import { useFocusStory } from '../../components/useFocusStory.js';
import { NotesPanel } from '../../components/NotesPanel.js';
import { DebugPanel } from '../../components/DebugPanel.js';
import { AndroidAppCard } from '../../components/AndroidAppCard.js';
import { ProjectDashboardDetails } from '../../components/ProjectDashboard.js';
import { AuditTable } from '../../components/AuditTable.js';
import { StoryBoard } from '../../components/StoryBoard.js';
import {
  BuildStatusBadge,
  DeployStatusBadge,
  ProjectStatusBadge,
  StoryStatusBadge,
} from '../../status.js';

interface SectionProps {
  projectId: string;
  /** Founder sign-off rights: the project owner, or staff acting on their behalf. */
  canApprove: boolean;
  /** Developer/admin rights: generate stories, set the repo, trigger builds. */
  canManage: boolean;
}

type Section =
  | 'dashboard'
  | 'questionnaire'
  | 'specification'
  | 'repository'
  | 'livetest'
  | 'stories'
  | 'board'
  | 'epics'
  | 'messages'
  | 'notes'
  | 'debug'
  | 'cost'
  | 'audit'
  | 'team';
const SECTIONS: { key: Section; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'questionnaire', label: 'Questionnaire' },
  { key: 'specification', label: 'Specification' },
  { key: 'repository', label: 'Repository' },
  { key: 'livetest', label: 'Live testing' },
  { key: 'stories', label: 'User stories' },
  { key: 'board', label: 'Board' },
  { key: 'epics', label: 'Epics' },
  { key: 'messages', label: 'Messages' },
  { key: 'notes', label: 'Notes' },
  { key: 'debug', label: 'Debug' },
  { key: 'cost', label: 'Usage & cost' },
  { key: 'audit', label: 'Activity' },
];

/** A small amber dot marking a nav item that needs attention. */
function NotificationDot({ title }: { title: string }) {
  return (
    <span
      title={title}
      aria-label={title}
      className="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-400 ring-2 ring-amber-400/30"
    />
  );
}

export function AdminProjectPage() {
  const { id } = useParams<{ id: string }>();
  const { data: user } = useMe();
  const project = useProject(id ?? '');
  const builds = useBuilds(id ?? '');
  const attention = useProjectAttention(id ?? '');
  // A deep link (e.g. a story link in the message feed) may name the tab to open
  // via ?section=; otherwise start on the dashboard.
  const [searchParams] = useSearchParams();
  const [section, setSection] = useState<Section>(() => {
    const requested = searchParams.get('section');
    return SECTIONS.some((s) => s.key === requested) || requested === 'team'
      ? (requested as Section)
      : 'dashboard';
  });
  // A story deep link (?story=<id>, from the message feed) opens the stories tab
  // even when this page is already mounted. Keyed on the story param alone, so a
  // manual tab click — which never sets it — is not overridden.
  const storyParam = searchParams.get('story');
  useEffect(() => {
    if (storyParam) setSection('stories');
  }, [storyParam]);

  if (!id) return <Alert>Missing project id.</Alert>;
  if (project.isPending) return <Spinner />;
  if (project.isError || !project.data) return <Alert>Could not load this project.</Alert>;

  const canManage = Boolean(user && isStaff(user.role));
  const canApprove = user?.id === project.data.ownerUserId || canManage;
  const props = { projectId: id, canApprove, canManage };
  // The build console should open the one that's running or, failing that, the
  // most recent build that has actually started (a queued build has no events
  // to show). builds are newest-first.
  const allBuilds = builds.data ?? [];
  const liveBuild =
    allBuilds.find((b) => b.status === 'running' || b.status === 'awaiting_approval') ??
    allBuilds.find((b) => b.status !== 'queued') ??
    allBuilds[0];

  // Only a super admin assigns developers to a project.
  const sections =
    user && isSuperAdmin(user.role)
      ? [...SECTIONS, { key: 'team' as const, label: 'Team' }]
      : SECTIONS;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to="/console" className="text-sm text-primary underline">
          ← Projects
        </Link>
        <div className="mt-1 flex items-center gap-3">
          <h1 className="font-title text-3xl tracking-wide">{project.data.name}</h1>
          <ProjectStatusBadge status={project.data.status} />
        </div>
        <p className="text-sm opacity-60">Created by {project.data.ownerEmail}</p>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row">
        <nav className="flex gap-1 overflow-x-auto sm:w-48 sm:flex-col">
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition ${
                section === s.key ? 'bg-primary text-white' : 'hover:bg-neutral'
              }`}
            >
              {s.label}
              {s.key === 'messages' && attention.data?.unreadMessages ? (
                <NotificationDot title="New messages" />
              ) : null}
            </button>
          ))}
          {liveBuild ? (
            <Link
              to={`/console/builds/${id}/${liveBuild.id}`}
              className="mt-2 flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary px-3 py-2 text-left text-sm font-medium text-white hover:opacity-90"
            >
              → Build console
              {attention.data?.needsApproval ? (
                <NotificationDot title="A build needs your approval" />
              ) : null}
            </Link>
          ) : null}
        </nav>

        <div className="min-w-0 flex-1">
          {section === 'dashboard' ? <ProjectDashboardDetails projectId={id} /> : null}
          {section === 'questionnaire' ? <QuestionnaireSection {...props} /> : null}
          {section === 'specification' ? <SpecSection {...props} /> : null}
          {section === 'repository' ? (
            <RepositorySection
              projectId={id}
              repo={project.data.githubRepoFullName}
              canManage={canManage}
            />
          ) : null}
          {section === 'livetest' ? <LiveTestingSection {...props} /> : null}
          {section === 'stories' ? <StoriesSection {...props} /> : null}
          {section === 'board' ? <BoardSection projectId={id} /> : null}
          {section === 'epics' ? <EpicsSection projectId={id} /> : null}
          {section === 'messages' ? <MessagesSection projectId={id} canManage={canManage} /> : null}
          {section === 'notes' ? <NotesSection projectId={id} /> : null}
          {section === 'debug' ? <DebugSection projectId={id} /> : null}
          {section === 'cost' ? <UsageSection projectId={id} /> : null}
          {section === 'audit' ? <AuditSection projectId={id} /> : null}
          {section === 'team' ? <AssignmentsSection projectId={id} /> : null}
        </div>
      </div>
    </div>
  );
}

/** The founder's questionnaire, read-only for staff. */
function QuestionnaireSection({ projectId }: SectionProps) {
  const { data, isPending, isError } = useQuestionnaire(projectId);
  const project = useProject(projectId);

  if (isPending) return <Card>{<Spinner />}</Card>;
  if (isError || !data) return <Card>{<Alert>Could not load the questionnaire.</Alert>}</Card>;

  const answers = data.answers;
  const doc = project.data?.sourceDocument;

  return (
    <Card>
      <h2 className="mb-3 font-title text-2xl tracking-wide">Questionnaire</h2>
      {doc ? (
        <div className="mb-4">
          <div className="text-sm font-medium">Uploaded document</div>
          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-neutral/60 p-3 text-xs">
            {doc}
          </pre>
        </div>
      ) : null}
      <dl className="flex flex-col gap-5">
        {data.questions.map((q) => (
          <div key={q.key} className="border-l-2 border-primary/40 pl-3">
            <dt className="text-xs font-semibold uppercase tracking-wide text-primary/80">
              {q.prompt}
            </dt>
            <dd className="mt-1.5 whitespace-pre-wrap rounded-md bg-neutral/40 p-3 text-sm">
              {answers[q.key] || <span className="italic opacity-60">Not answered</span>}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function SpecSection({ projectId, canApprove, canManage }: SectionProps) {
  const spec = useSpecification(projectId);
  const project = useProject(projectId);
  const generate = useGenerateSpec(projectId);
  const decide = useSpecDecision(projectId);
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);

  // Pull the finished spec in when generation completes (project polls itself).
  const specGenerating = project.data?.status === 'generating_spec';
  const wasSpecGenerating = useRef(specGenerating);
  useEffect(() => {
    if (wasSpecGenerating.current && !specGenerating) {
      void qc.invalidateQueries({ queryKey: ['specification', projectId] });
    }
    wasSpecGenerating.current = specGenerating;
  }, [specGenerating, projectId, qc]);

  const missing =
    generate.error instanceof ApiError && generate.error.code === 'missing_answers'
      ? ((generate.error.details as { missing?: string[] } | undefined)?.missing ?? [])
      : null;

  const approved = spec.data?.status === 'approved';
  const shared = project.data?.status === 'spec_shared';

  if (specGenerating) {
    return (
      <Card>
        <div className="flex items-center gap-3">
          <Spinner />
          <div>
            <h2 className="font-title text-2xl tracking-wide">Generating specification…</h2>
            <p className="text-sm opacity-70">
              The model is drafting the specification; this updates on its own when it’s ready.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-title text-2xl tracking-wide">Specification</h2>
          {spec.data && shared ? (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
                Awaiting founder approval
              </span>
              <Button
                variant="secondary"
                disabled={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {generate.isPending ? 'Regenerating…' : 'Regenerate'}
              </Button>
            </div>
          ) : spec.data && !approved ? (
            <div className="flex gap-2">
              <Button
                disabled={decide.isPending}
                onClick={() => decide.mutate({ decision: 'share' })}
                title="Present the finalised spec to the founder for approval"
              >
                Share with founder
              </Button>
              <Button
                variant="secondary"
                disabled={generate.isPending}
                onClick={() => generate.mutate()}
              >
                {generate.isPending ? 'Regenerating…' : 'Regenerate'}
              </Button>
            </div>
          ) : spec.data && approved && canManage ? (
            <Button
              variant="secondary"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'share' })}
              title="Undo the approval and send the spec back to the founder to approve again"
            >
              Reopen for approval
            </Button>
          ) : null}
        </div>

        {spec.isPending ? (
          <Spinner />
        ) : spec.data ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm opacity-60">
              Version {spec.data.version} · {spec.data.status.replace('_', ' ')}
            </p>
            <SpecView content={spec.data.content} downloadName={project.data?.name} />

            {approved ? (
              <Alert kind="info">Specification approved — ready to split into stories.</Alert>
            ) : canApprove ? (
              <div className="flex flex-col gap-2 border-t border-secondary/10 pt-4">
                <p className="text-sm font-medium">Do you approve this specification?</p>
                {showNote ? (
                  <textarea
                    className="min-h-16 rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    placeholder="What should change? The developer will see this."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                ) : null}
                <div className="flex gap-2">
                  <Button
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ decision: 'approve' })}
                  >
                    Approve
                  </Button>
                  {showNote ? (
                    <Button
                      variant="secondary"
                      disabled={decide.isPending || !note.trim()}
                      onClick={() =>
                        decide.mutate({ decision: 'request_changes', note: note.trim() })
                      }
                    >
                      Send request
                    </Button>
                  ) : (
                    <Button variant="secondary" onClick={() => setShowNote(true)}>
                      Request changes…
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="opacity-70">
              Once the questionnaire is answered, generate a technical specification from it.
            </p>
            {missing && missing.length > 0 ? (
              <Alert>Answer these required questions first: {missing.join(', ')}.</Alert>
            ) : generate.error instanceof ApiError &&
              generate.error.code === 'ai_not_configured' ? (
              <Alert>
                Add the Anthropic API key in <strong>Console → Keys</strong> to enable specification
                generation.
              </Alert>
            ) : generate.isError ? (
              <Alert>Could not generate the specification. Please try again.</Alert>
            ) : null}
            <div>
              <Button disabled={generate.isPending} onClick={() => generate.mutate()}>
                {generate.isPending ? 'Generating…' : 'Generate specification'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {spec.data ? (
        <Card>
          <h2 className="mb-3 font-title text-xl tracking-wide">Discussion</h2>
          <SpecComments projectId={projectId} canMarkInternal={canManage} />
        </Card>
      ) : null}
    </div>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Suggests `Aldemis/<firstname>-<project>` from the founder and project name. */
function suggestedRepo(project: Project | undefined): string {
  if (!project?.name) return '';
  const who =
    project.ownerFirstName?.trim() || (project.ownerEmail?.split('@')[0]?.split(/[+.]/)[0] ?? '');
  const parts = [who, project.name].map(slugify).filter(Boolean);
  return parts.length ? `Aldemis/${parts.join('-')}` : '';
}

function RepositorySection({
  projectId,
  repo,
  canManage,
}: {
  projectId: string;
  repo: string | null;
  canManage: boolean;
}) {
  const setRepo = useSetRepository(projectId);
  const project = useProject(projectId);
  const resetStories = useResetStories(projectId);
  const wipeRepo = useWipeRepo(projectId);
  const confirm = useConfirm();
  const [fullName, setFullName] = useState(repo ?? '');

  // Prefill a sensible default (founder + project name) once, when no repo is set.
  const prefilled = useRef(false);
  const suggestion = suggestedRepo(project.data);
  useEffect(() => {
    if (!prefilled.current && !repo && suggestion) {
      setFullName(suggestion);
      prefilled.current = true;
    }
  }, [suggestion, repo]);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (fullName.trim()) setRepo.mutate(fullName.trim());
  };

  const onReset = async (): Promise<void> => {
    if (
      await confirm({
        title: 'Reset stories for rebuild',
        message:
          'Reset ALL stories back to draft and add a Hello World story? This clears their built/tested state so the project can be rebuilt. The repository code is not touched.',
        confirmLabel: 'Reset stories',
      })
    )
      resetStories.mutate();
  };

  const onWipe = async (): Promise<void> => {
    if (
      await confirm({
        title: 'Wipe repository',
        message:
          'Permanently delete all code on the repository’s main branch, resetting it to an empty README? This cannot be undone.',
        confirmLabel: 'Wipe repository',
        danger: true,
      })
    )
      wipeRepo.mutate();
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <h2 className="mb-1 font-title text-2xl tracking-wide">Repository</h2>
        <p className="mb-3 text-sm opacity-70">
          The GitHub repo builds commit to. Format <code>owner/repo</code>.
        </p>
        <form className="flex flex-wrap items-end gap-3" onSubmit={onSubmit}>
          <input
            className="min-w-64 flex-1 rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder="Aldemis/customer-mvp"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Button
            type="submit"
            variant="secondary"
            disabled={setRepo.isPending || !fullName.trim()}
          >
            {setRepo.isPending ? 'Saving…' : 'Save repository'}
          </Button>
        </form>
        {setRepo.isError ? (
          <div className="mt-3">
            <Alert>Could not save. Use the form owner/repo.</Alert>
          </div>
        ) : setRepo.isSuccess ? (
          <p className="mt-3 text-sm text-primary">
            Repository set to {setRepo.data.githubRepoFullName}.
          </p>
        ) : null}
      </Card>

      {canManage ? (
        <Card>
          <h2 className="mb-1 font-title text-2xl tracking-wide">Rebuild &amp; reset</h2>
          <p className="mb-4 text-sm opacity-70">
            Tools to start a project over. These are destructive — use with care.
          </p>
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                disabled={resetStories.isPending}
                onClick={() => void onReset()}
              >
                {resetStories.isPending ? 'Resetting…' : 'Reset stories for rebuild'}
              </Button>
              <span className="text-sm opacity-70">
                Sends every story back to draft and adds a Hello World story. Leaves the repo code
                alone.
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                disabled={!repo || wipeRepo.isPending}
                title={
                  repo ? 'Delete all code on the repo’s main branch' : 'Set a repository first'
                }
                onClick={() => void onWipe()}
              >
                {wipeRepo.isPending ? 'Wiping…' : 'Wipe repository'}
              </Button>
              <span className="text-sm opacity-70">
                Resets the repo’s <code>main</code> branch to a fresh README — deletes all committed
                code.
              </span>
            </div>
          </div>
          {resetStories.isError ? (
            <div className="mt-3">
              <Alert>Could not reset the stories.</Alert>
            </div>
          ) : resetStories.isSuccess ? (
            <p className="mt-3 text-sm text-primary">Stories reset to draft.</p>
          ) : null}
          {wipeRepo.isError ? (
            <div className="mt-3">
              <Alert>Could not wipe the repository.</Alert>
            </div>
          ) : wipeRepo.isSuccess ? (
            <p className="mt-3 text-sm text-primary">Repository wiped.</p>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Live testing: the URL the deployed app is reachable at, its deploy status, and
 * a button to force a (re)deploy — for a developer to test the running app.
 */
function LiveTestingSection({ projectId, canManage }: SectionProps) {
  const deployment = useDeployment(projectId);
  const redeploy = useRedeploy(projectId);

  if (deployment.isPending)
    return (
      <Card>
        <Spinner />
      </Card>
    );
  if (deployment.isError || !deployment.data)
    return (
      <Card>
        <Alert>Could not load the deployment status.</Alert>
      </Card>
    );

  const d = deployment.data;
  const inFlight = d.status === 'provisioning' || d.status === 'building';
  const neverDeployed = !d.liveUrl && d.status === 'none';

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="font-title text-2xl tracking-wide">Live testing</h2>
        <DeployStatusBadge status={d.status} />
      </div>

      {d.liveUrl ? (
        <div className="mb-4 rounded-md bg-neutral/40 p-3">
          <div className="text-xs uppercase tracking-wide opacity-60">Live URL</div>
          <a
            href={d.liveUrl}
            target="_blank"
            rel="noreferrer"
            className="break-all font-medium text-primary underline"
          >
            {d.liveUrl}
          </a>
          {d.lastDeployedAt ? (
            <p className="mt-1 text-xs opacity-60">
              Last deployed {new Date(d.lastDeployedAt).toLocaleString()}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mb-4 text-sm opacity-70">
          {d.hasRepository
            ? inFlight
              ? 'Deploying — the live URL will appear here once it is up.'
              : 'Not deployed yet. Deploy to get a live URL to test against.'
            : 'Set a repository (Repository tab) first — the app is deployed from it.'}
        </p>
      )}

      {d.adminLiveUrl ? (
        <div className="mb-4 rounded-md bg-neutral/40 p-3">
          <div className="text-xs uppercase tracking-wide opacity-60">Admin portal</div>
          <a
            href={d.adminLiveUrl}
            target="_blank"
            rel="noreferrer"
            className="break-all font-medium text-primary underline"
          >
            {d.adminLiveUrl}
          </a>
        </div>
      ) : null}

      {d.status === 'failed' && d.latest?.detail ? (
        <div className="mb-3">
          <Alert>Last deploy failed: {d.latest.detail}</Alert>
        </div>
      ) : null}

      {canManage ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={!d.hasRepository || redeploy.isPending || inFlight}
            onClick={() => redeploy.mutate()}
            title={
              d.hasRepository
                ? 'Force a deploy from the current main branch'
                : 'Set a repository first'
            }
          >
            {redeploy.isPending || inFlight
              ? 'Deploying…'
              : neverDeployed
                ? 'Deploy now'
                : 'Redeploy'}
          </Button>
          {redeploy.isError ? (
            <span className="text-sm text-red-700">Could not start a deploy.</span>
          ) : null}
        </div>
      ) : null}

      <p className="mt-4 text-xs opacity-60">
        The app deploys automatically when a story build succeeds; use this to force one from the
        latest <code>main</code>.
      </p>

      {d.liveUrl ? (
        <div className="mt-5 border-t border-secondary/10 pt-4">
          <h3 className="mb-2 font-title text-lg tracking-wide">Mobile</h3>
          <AndroidAppCard projectId={projectId} canBuild={canManage} />
        </div>
      ) : null}
    </Card>
  );
}

function StoriesSection({ projectId, canApprove, canManage }: SectionProps) {
  const stories = useStories(projectId);
  const spec = useSpecification(projectId);
  const project = useProject(projectId);
  const generate = useGenerateStories(projectId);
  const builds = useBuilds(projectId);
  const reorder = useReorderStories(projectId);
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);
  // Deep link from the message feed (?story=<id>): scroll to and highlight it.
  const focusStoryId = useFocusStory(!stories.isPending);

  // Pull the finished stories in when generation completes (project polls itself).
  const storiesGenerating = project.data?.status === 'generating_stories';
  const wasStoriesGenerating = useRef(storiesGenerating);
  useEffect(() => {
    if (wasStoriesGenerating.current && !storiesGenerating) {
      void qc.invalidateQueries({ queryKey: ['stories', projectId] });
    }
    wasStoriesGenerating.current = storiesGenerating;
  }, [storiesGenerating, projectId, qc]);

  const specApproved = spec.data?.status === 'approved';
  const ordered = stories.data ?? [];
  const hasStories = ordered.length > 0;

  if (storiesGenerating) {
    return (
      <Card>
        <div className="flex items-center gap-3">
          <Spinner />
          <div>
            <h2 className="font-title text-2xl tracking-wide">Generating user stories…</h2>
            <p className="text-sm opacity-70">
              The model is splitting the specification into stories; this updates on its own when
              it’s ready.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Latest build per story, so each row can link to its live console.
  const latestBuild = new Map<string, Build>();
  for (const build of builds.data ?? []) {
    if (!latestBuild.has(build.userStoryId)) latestBuild.set(build.userStoryId, build);
  }

  // Any approved story can be built — including one that was built before and
  // has since been reset and re-approved (a rebuild). Status is the gate; a
  // prior build record must not exclude it, or a reset project can never rebuild.
  const buildable = ordered.filter((s) => s.status === 'approved');

  // Tested (accepted) stories move to a separate "Done" section; the rest stay
  // active. Reordering swaps two active stories by their position in the full
  // list, so the done stories keep their place (and their numbers).
  const withIndex = ordered.map((story, i) => ({ story, i }));
  const activeEntries = withIndex.filter((e) => e.story.status !== 'accepted');
  const doneEntries = withIndex.filter((e) => e.story.status === 'accepted');
  // Open the Done section when the deep-linked story lives there.
  const doneOpen =
    showDone || (focusStoryId ? doneEntries.some((e) => e.story.id === focusStoryId) : false);

  const moveActive = (pos: number, dir: -1 | 1): void => {
    const target = pos + dir;
    if (target < 0 || target >= activeEntries.length) return;
    const a = activeEntries[pos]!.i;
    const b = activeEntries[target]!.i;
    const next = [...ordered];
    [next[a], next[b]] = [next[b]!, next[a]!];
    reorder.mutate(next.map((s) => s.id));
  };

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-title text-2xl tracking-wide">User stories</h2>
        <div className="flex gap-2">
          {canManage && !hasStories ? (
            <Button
              disabled={generate.isPending || !specApproved}
              onClick={() => generate.mutate()}
              title={specApproved ? undefined : 'Approve the specification first'}
            >
              {generate.isPending ? 'Splitting…' : 'Generate stories'}
            </Button>
          ) : null}
          {canManage && spec.data ? (
            <Button variant="secondary" onClick={() => setAdding((v) => !v)}>
              {adding ? 'Close' : '+ Add story'}
            </Button>
          ) : null}
        </div>
      </div>

      {adding ? (
        <div className="mb-4">
          <NewStoryForm projectId={projectId} onClose={() => setAdding(false)} />
        </div>
      ) : null}

      {canManage && buildable.length > 0 ? (
        <BuildPanel projectId={projectId} buildable={buildable} />
      ) : null}

      {stories.isPending ? (
        <Spinner />
      ) : !hasStories ? (
        <p className="opacity-70">
          {!specApproved
            ? 'The specification needs to be approved before it is split into stories.'
            : 'No stories yet. Generate them from the approved specification.'}
        </p>
      ) : (
        <>
          {activeEntries.length > 0 ? (
            <ol className="flex flex-col gap-3">
              {activeEntries.map(({ story }, pos) => {
                const showEpic = pos === 0 || activeEntries[pos - 1]?.story.epic !== story.epic;
                return (
                  <div
                    key={story.id}
                    id={`story-${story.id}`}
                    className="flex flex-col gap-2 rounded-md transition"
                  >
                    {showEpic ? (
                      <h3 className="mt-2 text-sm font-semibold uppercase tracking-wide text-primary">
                        {story.epic || 'Ungrouped'}
                      </h3>
                    ) : null}
                    <StoryRow
                      projectId={projectId}
                      story={story}
                      build={latestBuild.get(story.id)}
                      canApprove={canApprove}
                      canManage={canManage}
                      onMoveUp={canManage && pos > 0 ? () => moveActive(pos, -1) : undefined}
                      onMoveDown={
                        canManage && pos < activeEntries.length - 1
                          ? () => moveActive(pos, 1)
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </ol>
          ) : (
            <p className="opacity-70">Every story has been built and tested. 🎉</p>
          )}

          {doneEntries.length > 0 ? (
            <div className="mt-6 border-t border-secondary/10 pt-4">
              <button
                className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-secondary/70 hover:text-secondary"
                onClick={() => setShowDone((v) => !v)}
              >
                <span className="text-xs">{doneOpen ? '▾' : '▸'}</span>
                Done — {doneEntries.length} tested {doneEntries.length === 1 ? 'story' : 'stories'}
              </button>
              {doneOpen ? (
                <ol className="mt-3 flex flex-col gap-3">
                  {doneEntries.map(({ story }) => (
                    <div
                      key={story.id}
                      id={`story-${story.id}`}
                      className="flex flex-col gap-2 rounded-md transition"
                    >
                      <StoryRow
                        projectId={projectId}
                        story={story}
                        build={latestBuild.get(story.id)}
                        canApprove={canApprove}
                        canManage={canManage}
                      />
                    </div>
                  ))}
                </ol>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

/** Formats equivalent developer hours compactly, e.g. `6h` or `6.5h`. */
function hoursLabel(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

/**
 * Lets a developer pick which approved stories to build now and shows what that
 * choice does to the founder's monthly equivalent-developer-hours budget: the
 * hours the selected stories add, what has already been spent this month, and
 * the projected total against the tier allowance. It reports over-budget but
 * does not block — the developer decides.
 */
function BuildPanel({ projectId, buildable }: { projectId: string; buildable: Story[] }) {
  const batch = useBatchBuild(projectId);
  const hours = useHoursStatus(projectId);
  const [autoApprove, setAutoApprove] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(buildable.map((s) => s.id)));

  // Default to all currently-buildable stories; re-sync when that set changes
  // (a build starting removes a story, a new approval adds one).
  const buildableKey = buildable.map((s) => s.id).join(',');
  useEffect(() => {
    setSelected(new Set(buildableKey ? buildableKey.split(',') : []));
  }, [buildableKey]);

  const toggle = (id: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chosen = buildable.filter((s) => selected.has(s.id));
  const selectedHours = chosen.reduce((sum, s) => sum + (s.equivalentDevHours ?? 0), 0);
  const someUnestimated = chosen.some((s) => s.equivalentDevHours === null);

  const status = hours.data;
  const spent = status?.spentThisMonth ?? 0;
  const allowance = status?.allowanceHours ?? 0;
  const projected = spent + selectedHours;
  const overBy = allowance > 0 ? Math.max(0, projected - allowance) : 0;

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-md bg-neutral/50 p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Build approved stories</span>
        <button
          className="text-xs text-primary underline"
          onClick={() =>
            setSelected((s) =>
              s.size === buildable.length ? new Set() : new Set(buildable.map((b) => b.id)),
            )
          }
        >
          {selected.size === buildable.length ? 'Clear all' : 'Select all'}
        </button>
      </div>

      <ul className="flex flex-col gap-1">
        {buildable.map((s) => (
          <li key={s.id}>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
              <span className="flex-1">{s.title}</span>
              <span className="opacity-60">
                {s.equivalentDevHours !== null ? hoursLabel(s.equivalentDevHours) : '—'}
              </span>
            </label>
          </li>
        ))}
      </ul>

      {/* What the selection does to the monthly hours budget (req: show total,
          already-spent, and the projected new total). */}
      <div className="rounded-md bg-white p-3 text-sm">
        <div className="flex flex-wrap justify-between gap-x-6 gap-y-1">
          <span>
            Selected: <span className="font-medium">{hoursLabel(selectedHours)}</span> across{' '}
            {chosen.length} {chosen.length === 1 ? 'story' : 'stories'}
            {someUnestimated ? <span className="opacity-60"> (some unestimated)</span> : null}
          </span>
          {allowance > 0 ? (
            <span className="opacity-70">
              {hoursLabel(spent)} spent this month →{' '}
              <span className="font-medium">{hoursLabel(projected)}</span> of{' '}
              {hoursLabel(allowance)}
            </span>
          ) : null}
        </div>
        {overBy > 0 ? (
          <p className="mt-1 text-red-700">
            This would exceed the monthly allowance by {hoursLabel(overBy)}.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoApprove}
            onChange={(e) => setAutoApprove(e.target.checked)}
          />
          Always allow (auto-approve commands)
        </label>
        <Button
          className="ml-auto"
          disabled={batch.isPending || selected.size === 0}
          onClick={() => batch.mutate({ storyIds: [...selected], autoApprove })}
        >
          {batch.isPending
            ? 'Queueing…'
            : `Build ${selected.size} ${selected.size === 1 ? 'story' : 'stories'}`}
        </Button>
      </div>
      {batch.isError ? <Alert>Could not queue the builds.</Alert> : null}
    </div>
  );
}

function StoryRow({
  projectId,
  story,
  build,
  canApprove,
  canManage,
  onMoveUp,
  onMoveDown,
}: {
  projectId: string;
  story: Story;
  build: Build | undefined;
  canApprove: boolean;
  canManage: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const decide = useStoryDecision(projectId);
  const start = useStartBuild(projectId);
  const del = useDeleteStory(projectId);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [showComments, setShowComments] = useState(false);

  if (editing) {
    return <StoryEditForm projectId={projectId} story={story} onClose={() => setEditing(false)} />;
  }

  const onDelete = async (): Promise<void> => {
    if (
      await confirm({
        title: 'Delete story',
        message: `Delete story “${story.title}”? This cannot be undone.`,
        confirmLabel: 'Delete',
        danger: true,
      })
    ) {
      del.mutate(story.id);
    }
  };

  return (
    <li className="rounded-md bg-neutral/50 p-4">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-sm opacity-60">#{story.sequence}</span>
        <span className="font-medium">{story.title}</span>
        <span className="ml-auto flex items-center gap-2">
          {canManage ? (
            <>
              <button
                className="rounded px-1.5 text-secondary/60 hover:text-secondary disabled:opacity-30"
                disabled={!onMoveUp}
                onClick={onMoveUp}
                aria-label="Move up"
              >
                ↑
              </button>
              <button
                className="rounded px-1.5 text-secondary/60 hover:text-secondary disabled:opacity-30"
                disabled={!onMoveDown}
                onClick={onMoveDown}
                aria-label="Move down"
              >
                ↓
              </button>
              <button className="text-sm text-primary underline" onClick={() => setEditing(true)}>
                Edit
              </button>
              <button
                className="text-sm text-red-700 underline disabled:opacity-40"
                disabled={del.isPending || story.status === 'building'}
                title={
                  story.status === 'building' ? 'Cannot delete a story while it builds' : undefined
                }
                onClick={() => void onDelete()}
              >
                Delete
              </button>
            </>
          ) : null}
          <StoryStatusBadge status={story.status} />
          {build ? <BuildStatusBadge status={build.status} /> : null}
        </span>
      </div>
      {/* Equivalent developer time, right under the title — auto-estimated. */}
      {story.equivalentDevHours !== null ? (
        <p className="mb-2 text-sm font-medium text-primary">
          ≈ {hoursLabel(story.equivalentDevHours)} of developer time
        </p>
      ) : (
        <p className="mb-2 text-sm italic opacity-50">Developer time not estimated yet</p>
      )}
      <p className="text-sm leading-relaxed">
        As <em>{story.asA}</em>, I want {story.iWant}, so that {story.soThat}.
      </p>
      {story.acceptanceCriteria.length > 0 ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm opacity-80">
          {story.acceptanceCriteria.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {/* Developer readies a draft story for the founder to sign off. */}
        {canManage && (story.status === 'draft' || story.status === 'in_review') ? (
          <Button
            variant="secondary"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ storyId: story.id, decision: 'ready' })}
          >
            Mark ready for approval
          </Button>
        ) : null}

        {/* Founder signs off on a ready story; staff can override (hence grey). */}
        {canApprove && story.status === 'ready' ? (
          <Button
            variant="secondary"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ storyId: story.id, decision: 'approve' })}
            title="Normally the founder approves this; use only to override on their behalf"
          >
            Approve story
          </Button>
        ) : null}

        {/* An approved story can be built — or rebuilt, if it was built before
            and has since been reset and re-approved. A rebuild is a low-emphasis
            redo, so it uses the muted (grey) button. */}
        {story.status === 'approved' && canApprove ? (
          <Button
            variant={build ? 'muted' : 'secondary'}
            disabled={start.isPending}
            onClick={() =>
              start.mutate(
                { storyId: story.id },
                {
                  onSuccess: (b) =>
                    canManage ? void navigate(`/console/builds/${projectId}/${b.id}`) : undefined,
                },
              )
            }
          >
            {start.isPending ? 'Starting…' : build ? 'Rebuild this story' : 'Build this story'}
          </Button>
        ) : null}

        {/* Undo a story approved by mistake, sending it back for approval. */}
        {story.status === 'approved' && !build && canManage ? (
          <Button
            variant="secondary"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ storyId: story.id, decision: 'unapprove' })}
            title="Undo this approval, sending the story back for approval"
          >
            Unapprove
          </Button>
        ) : null}

        {/* A built story is "In test"; a developer marks it tested (accepted). */}
        {story.status === 'built' && canManage ? (
          <Button
            disabled={decide.isPending}
            onClick={() => decide.mutate({ storyId: story.id, decision: 'accept' })}
            title="Mark this story tested"
          >
            Mark tested
          </Button>
        ) : null}

        {/* A built or tested story can be sent back to approved to rebuild it —
            per story, so you can rebuild only the ones you choose. */}
        {(story.status === 'built' || story.status === 'accepted') && canManage ? (
          <Button
            variant="danger"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ storyId: story.id, decision: 'reopen' })}
            title="Send this story back to approved so it can be rebuilt — this discards its built/tested state"
          >
            Reset to approved
          </Button>
        ) : null}

        {build ? (
          canManage ? (
            <Link
              to={`/console/builds/${projectId}/${build.id}`}
              className="text-sm text-primary underline"
            >
              Open build console
            </Link>
          ) : (
            <span className="text-sm opacity-70">A developer is supervising the build.</span>
          )
        ) : null}
      </div>

      <button
        className="mt-3 text-sm text-primary underline"
        onClick={() => setShowComments((v) => !v)}
      >
        {showComments ? 'Hide comments' : 'Comments'}
      </button>
      {showComments ? (
        <div className="mt-3 border-t border-secondary/10 pt-3">
          <StoryComments projectId={projectId} storyId={story.id} canMarkInternal={canManage} />
        </div>
      ) : null}
    </li>
  );
}

function StoryEditForm({
  projectId,
  story,
  onClose,
}: {
  projectId: string;
  story: Story;
  onClose: () => void;
}) {
  const update = useUpdateStory(projectId);
  const [title, setTitle] = useState(story.title);
  const [epicId, setEpicId] = useState<string | null>(story.epicId);
  const [asA, setAsA] = useState(story.asA);
  const [iWant, setIWant] = useState(story.iWant);
  const [soThat, setSoThat] = useState(story.soThat);
  const [criteria, setCriteria] = useState(story.acceptanceCriteria.join('\n'));

  const onSave = (): void => {
    update.mutate(
      {
        storyId: story.id,
        edit: {
          title: title.trim(),
          epicId,
          asA,
          iWant,
          soThat,
          acceptanceCriteria: criteria
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <li className="flex flex-col gap-3 rounded-md bg-white p-4 ring-1 ring-primary/30">
      <Field label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <EpicSelect projectId={projectId} value={epicId} onChange={setEpicId} />
      <Field label="As a…" value={asA} onChange={(e) => setAsA(e.target.value)} />
      <Field label="I want…" value={iWant} onChange={(e) => setIWant(e.target.value)} />
      <Field label="So that…" value={soThat} onChange={(e) => setSoThat(e.target.value)} />
      <TextArea
        label="Acceptance criteria"
        hint="One per line"
        value={criteria}
        onChange={(e) => setCriteria(e.target.value)}
      />
      <p className="text-xs opacity-60">Developer-time is estimated automatically when you save.</p>
      {update.isError ? <Alert>Could not save the story.</Alert> : null}
      <div className="flex gap-2">
        <Button disabled={update.isPending || !title.trim()} onClick={onSave}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </li>
  );
}

function NewStoryForm({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const create = useCreateStory(projectId);
  const [title, setTitle] = useState('');
  const [epicId, setEpicId] = useState<string | null>(null);
  const [asA, setAsA] = useState('');
  const [iWant, setIWant] = useState('');
  const [soThat, setSoThat] = useState('');
  const [criteria, setCriteria] = useState('');

  const valid = Boolean(title.trim() && asA.trim() && iWant.trim() && soThat.trim());

  const onCreate = (): void => {
    create.mutate(
      {
        title: title.trim(),
        epicId,
        asA: asA.trim(),
        iWant: iWant.trim(),
        soThat: soThat.trim(),
        acceptanceCriteria: criteria
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="flex flex-col gap-3 rounded-md bg-white p-4 ring-1 ring-primary/30">
      <h3 className="font-medium">New user story</h3>
      <Field label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <EpicSelect projectId={projectId} value={epicId} onChange={setEpicId} />
      <Field label="As a…" value={asA} onChange={(e) => setAsA(e.target.value)} />
      <Field label="I want…" value={iWant} onChange={(e) => setIWant(e.target.value)} />
      <Field label="So that…" value={soThat} onChange={(e) => setSoThat(e.target.value)} />
      <TextArea
        label="Acceptance criteria"
        hint="One per line"
        value={criteria}
        onChange={(e) => setCriteria(e.target.value)}
      />
      <p className="text-xs opacity-60">Developer-time is estimated automatically on creation.</p>
      {create.isError ? <Alert>Could not create the story.</Alert> : null}
      <div className="flex gap-2">
        <Button disabled={create.isPending || !valid} onClick={onCreate}>
          {create.isPending ? 'Creating…' : 'Create story'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function EpicSelect({
  projectId,
  value,
  onChange,
}: {
  projectId: string;
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const epics = useEpics(projectId);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">Epic</span>
      <select
        className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">No epic</option>
        {(epics.data ?? []).map((ep) => (
          <option key={ep.id} value={ep.id}>
            {ep.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function EpicsSection({ projectId }: { projectId: string }) {
  const epics = useEpics(projectId);
  const create = useCreateEpic(projectId);
  const [name, setName] = useState('');
  const [managing, setManaging] = useState<string | null>(null);

  const onCreate = (event: FormEvent): void => {
    event.preventDefault();
    if (!name.trim()) return;
    create.mutate(name.trim(), { onSuccess: () => setName('') });
  };
  const taken = create.error instanceof ApiError && create.error.code === 'epic_name_taken';

  return (
    <Card>
      <h2 className="mb-1 font-title text-2xl tracking-wide">Epics</h2>
      <p className="mb-4 text-sm opacity-70">
        Group user stories into epics. Pick an epic on a story, or use “Manage” to assign several
        unassigned stories to an epic at once.
      </p>

      <form className="mb-3 flex flex-wrap items-end gap-3" onSubmit={onCreate}>
        <div className="min-w-56 flex-1">
          <Field
            label="New epic"
            placeholder="e.g. Authentication"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          {create.isPending ? 'Adding…' : 'Add epic'}
        </Button>
      </form>
      {taken ? (
        <div className="mb-3">
          <Alert>An epic with that name already exists.</Alert>
        </div>
      ) : null}

      {epics.isPending ? (
        <Spinner />
      ) : epics.isError ? (
        <Alert>Could not load epics.</Alert>
      ) : epics.data.length === 0 ? (
        <p className="opacity-70">No epics yet — add one above.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {epics.data.map((ep) => (
            <EpicRow
              key={ep.id}
              projectId={projectId}
              epic={ep}
              managing={managing === ep.id}
              onToggleManage={() => setManaging(managing === ep.id ? null : ep.id)}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function EpicRow({
  projectId,
  epic,
  managing,
  onToggleManage,
}: {
  projectId: string;
  epic: { id: string; name: string; storyCount: number };
  managing: boolean;
  onToggleManage: () => void;
}) {
  const rename = useRenameEpic(projectId);
  const del = useDeleteEpic(projectId);
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(epic.name);

  const onDelete = async (): Promise<void> => {
    if (
      await confirm({
        title: 'Delete epic',
        message: `Delete epic “${epic.name}”? Its stories become unassigned.`,
        confirmLabel: 'Delete',
        danger: true,
      })
    ) {
      del.mutate(epic.id);
    }
  };

  return (
    <li className="rounded-md bg-neutral/50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {editing ? (
          <>
            <input
              className="flex-1 rounded border border-secondary/20 bg-white px-2 py-1 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={rename.isPending || !name.trim()}
              onClick={() =>
                rename.mutate(
                  { epicId: epic.id, name: name.trim() },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              Save
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setName(epic.name);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <span className="flex-1 font-medium">{epic.name}</span>
            <span className="text-xs opacity-60">
              {epic.storyCount} {epic.storyCount === 1 ? 'story' : 'stories'}
            </span>
            <button className="text-sm text-primary underline" onClick={onToggleManage}>
              {managing ? 'Close' : 'Manage'}
            </button>
            <button className="text-sm text-primary underline" onClick={() => setEditing(true)}>
              Rename
            </button>
            <button className="text-sm text-red-700 underline" onClick={() => void onDelete()}>
              Delete
            </button>
          </>
        )}
      </div>
      {managing ? (
        <div className="mt-3 border-t border-secondary/10 pt-3">
          <EpicManage projectId={projectId} epicId={epic.id} />
        </div>
      ) : null}
    </li>
  );
}

function EpicManage({ projectId, epicId }: { projectId: string; epicId: string }) {
  const unassigned = useUnassignedStories(projectId);
  const assign = useAssignStoriesToEpic(projectId);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (unassigned.isPending) return <Spinner />;
  if (unassigned.isError || !unassigned.data) return <Alert>Could not load stories.</Alert>;
  if (unassigned.data.length === 0) {
    return <p className="text-sm opacity-60">No unassigned stories.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">Tick unassigned stories to add to this epic:</p>
      <ul className="flex flex-col gap-1">
        {unassigned.data.map((s) => (
          <li key={s.id}>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
              <span className="opacity-60">#{s.sequence}</span> {s.title}
            </label>
          </li>
        ))}
      </ul>
      <div>
        <Button
          disabled={assign.isPending || selected.size === 0}
          onClick={() =>
            assign.mutate(
              { epicId, storyIds: [...selected] },
              { onSuccess: () => setSelected(new Set()) },
            )
          }
        >
          {assign.isPending
            ? 'Assigning…'
            : `Assign ${selected.size} ${selected.size === 1 ? 'story' : 'stories'}`}
        </Button>
      </div>
    </div>
  );
}

function MessagesSection({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const attention = useProjectAttention(projectId);
  const markRead = useMarkMessagesRead(projectId);
  return (
    <Card>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-title text-2xl tracking-wide">Messages</h2>
        {attention.data?.unreadMessages ? (
          <Button
            variant="secondary"
            disabled={markRead.isPending}
            onClick={() => markRead.mutate()}
          >
            {markRead.isPending ? 'Marking…' : 'Mark as read'}
          </Button>
        ) : null}
      </div>
      <p className="mb-4 text-sm opacity-70">
        The project’s conversation with the founder. Story comments show up here too. Mark a message
        internal to keep it developer-only.
      </p>
      <ProjectMessages
        projectId={projectId}
        canMarkInternal={canManage}
        storyHref={(storyId) => `/console/projects/${projectId}?section=stories&story=${storyId}`}
      />
    </Card>
  );
}

function NotesSection({ projectId }: { projectId: string }) {
  return (
    <Card>
      <h2 className="mb-1 font-title text-2xl tracking-wide">Notes</h2>
      <p className="mb-4 text-sm opacity-70">
        A private scratchpad for this project, shared across staff. The founder never sees it.
      </p>
      <NotesPanel projectId={projectId} />
    </Card>
  );
}

function DebugSection({ projectId }: { projectId: string }) {
  return (
    <Card>
      <h2 className="mb-1 font-title text-2xl tracking-wide">Debug</h2>
      <p className="mb-4 text-sm opacity-70">
        Ask Claude about this project — it sees the specification, the stories and their statuses,
        the selected build’s transcript, and the deployment status, and it can check live AWS
        (CloudWatch logs, ECS health, deploy logs). You can also describe a bug or change and have
        it drafted into a user story to add straight to the backlog.
      </p>
      <DebugPanel projectId={projectId} />
    </Card>
  );
}

function AssignmentsSection({ projectId }: { projectId: string }) {
  const q = useAssignments(projectId);
  const assign = useAssignDeveloper(projectId);
  const unassign = useUnassignDeveloper(projectId);
  const [pick, setPick] = useState('');

  if (q.isPending) return <Card>{<Spinner />}</Card>;
  if (q.isError || !q.data) return <Card>{<Alert>Could not load the team.</Alert>}</Card>;

  const assigned = q.data.assignments;
  const assignedIds = new Set(assigned.map((d) => d.userId));
  const available = q.data.developers.filter((d) => !assignedIds.has(d.userId));

  return (
    <Card>
      <h2 className="mb-1 font-title text-2xl tracking-wide">Team</h2>
      <p className="mb-4 text-sm opacity-70">
        Developers assigned here can see and work on this project. Everyone else on the developer
        team cannot.
      </p>

      {assigned.length === 0 ? (
        <p className="opacity-70">No developers assigned yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {assigned.map((d) => (
            <li key={d.userId} className="flex items-center gap-3 rounded-md bg-neutral/50 p-3">
              <span className="flex-1 truncate text-sm">{d.email}</span>
              <Button
                variant="secondary"
                disabled={unassign.isPending}
                onClick={() => unassign.mutate(d.userId)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-secondary/10 pt-4">
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Assign a developer</span>
            <select
              className="rounded-md border border-secondary/20 bg-white px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
            >
              <option value="">Choose a developer…</option>
              {available.map((d) => (
                <option key={d.userId} value={d.userId}>
                  {d.email}
                </option>
              ))}
            </select>
          </label>
          <Button
            disabled={!pick || assign.isPending}
            onClick={() => assign.mutate(pick, { onSuccess: () => setPick('') })}
          >
            {assign.isPending ? 'Assigning…' : 'Assign'}
          </Button>
        </div>
      ) : assigned.length > 0 ? (
        <p className="mt-4 text-sm opacity-60">Every developer is already assigned.</p>
      ) : (
        <p className="mt-4 text-sm opacity-60">
          No developer accounts yet — invite one from the console.
        </p>
      )}
    </Card>
  );
}

function BoardSection({ projectId }: { projectId: string }) {
  const stories = useStories(projectId);
  if (stories.isPending) return <Card>{<Spinner />}</Card>;
  if (stories.isError) return <Card>{<Alert>Could not load the board.</Alert>}</Card>;
  return <StoryBoard stories={stories.data ?? []} />;
}

function AuditSection({ projectId }: { projectId: string }) {
  const audit = useProjectAudit(projectId);
  return (
    <Card>
      <h2 className="mb-1 font-title text-2xl tracking-wide">Activity</h2>
      <p className="mb-4 text-sm opacity-70">
        Everything that happened on this project — who triggered which story, what was approved, and
        when.
      </p>
      {audit.isPending ? (
        <Spinner />
      ) : audit.isError || !audit.data ? (
        <Alert>Could not load the activity log.</Alert>
      ) : (
        <AuditTable entries={audit.data} />
      )}
    </Card>
  );
}

function UsageSection({ projectId }: { projectId: string }) {
  const usage = useUsage(projectId);
  if (usage.isPending) return <Card>{<Spinner />}</Card>;
  if (!usage.data || usage.data.builds.length === 0) {
    return (
      <Card>
        <h2 className="mb-2 font-title text-2xl tracking-wide">Usage &amp; cost</h2>
        <p className="opacity-70">No builds yet — usage appears once a story is built.</p>
      </Card>
    );
  }

  const money = (n: number): string => `$${n.toFixed(4)}`;
  const { totals, estimatedCostUsd, builds } = usage.data;

  return (
    <Card>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-title text-2xl tracking-wide">Usage &amp; cost</h2>
        <span className="text-sm opacity-60">estimated · {builds.length} builds</span>
      </div>
      <div className="mb-4 flex flex-wrap gap-6">
        <Stat label="Input tokens" value={totals.inputTokens.toLocaleString()} />
        <Stat label="Output tokens" value={totals.outputTokens.toLocaleString()} />
        <Stat label="Cache read" value={totals.cacheReadTokens.toLocaleString()} />
        <Stat label="Estimated cost" value={money(estimatedCostUsd)} highlight />
      </div>
      <ul className="divide-y divide-secondary/10 text-sm">
        {builds.map((b) => (
          <li key={b.buildId} className="flex items-center gap-3 py-1.5">
            <span className="flex-1 truncate">{b.storyTitle}</span>
            <BuildStatusBadge status={b.status} />
            <span className="w-32 text-right opacity-70">
              {(b.inputTokens + b.outputTokens).toLocaleString()} tok
            </span>
            <span className="w-20 text-right font-medium">{money(b.estimatedCostUsd)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide opacity-60">{label}</div>
      <div className={`text-xl ${highlight ? 'font-semibold text-primary' : ''}`}>{value}</div>
    </div>
  );
}
